import type { CallOutcomeExtra, CallOutcomeOption } from './CallOutcomePicker';
import type { SoftphoneCarrierFailure, SoftphoneStatus } from './softphoneClient.types';
import type { VoicemailDropControl } from './VoicemailDropButton';
import { ContactAvatar } from '../Avatar/ContactAvatar';
import { cn } from '../cn';
import Button from '../Input2/Button';
import { Communication, General, Media, Users } from '../StrokeIcons';
import { CallerNumberLine } from './CallerNumberLine';
import { CallFailedNotice } from './CallFailedNotice';
import { CallOutcomePicker } from './CallOutcomePicker';
import { SoftphoneKeypad } from './SoftphoneKeypad';
import { formatDuration, resolvePostCallView } from './softphoneStatus';
import { VoicemailDropButton } from './VoicemailDropButton';

export type { CallOutcomeExtra, CallOutcomeOption };

export interface SoftphoneProps {
  contactName?: string;
  phoneNumber: string;
  status: SoftphoneStatus;
  durationSec: number;
  isMuted: boolean;
  error: string | null;
  /**
   * The carrier refused the just-ended call (see `SoftphoneCarrierFailure`). Renders the
   * `CallFailedNotice` in place of "Call ended"; an account-level block (Twilio 13225) also
   * replaces the outcome picker with a plain Close — the prospect was never rung.
   */
  carrierFailure?: SoftphoneCarrierFailure | null;
  /**
   * The disposition the server set automatically for the ended call (`useSoftphone().autoOutcome`).
   * Present = the outcome step shows it as a confirmable chip instead of asking the rep.
   */
  autoOutcome?: string | null;
  /** When true, show a recording-consent indicator while the call is live. */
  isRecording?: boolean;
  /**
   * The carrier's own dialer runs the call (JustCall): mute, keypad and hang-up live there, so ours
   * are hidden and the rep is pointed at it instead.
   */
  embeddedCarrierDialer?: boolean;
  /** A short instruction while the call is set up (e.g. "Sign in to JustCall…"). */
  notice?: string | null;
  /** Contact photo for the avatar; falls back to initials from contactName when absent. */
  imageSrc?: string | null;
  /**
   * The number being dialed is the contact's org ("firm") number, used because the person has no
   * direct number of their own. Renders a prominent "Firm number" badge so the rep knows they're
   * reaching the company, not the person.
   */
  isFirmNumber?: boolean;
  /** The number this call is/was placed from. Renders a "Calling from …" line; hidden when absent. */
  callerNumber?: string | null;
  /**
   * The contact's pinned from-number is no longer in the rep's bucket: show a subtle warning.
   * The number is still what this call goes out from. Rotation pulls the pin back into the bucket
   * on the next unanswered call — but NOT once the contact's number has settled (they answered, or
   * the rep chose it via Change), so a settled-then-released number needs a manual re-pin.
   */
  callerNumberStale?: boolean;
  /** Present = render the small guarded "Change" affordance; disabled while a call is live. */
  onChangeNumber?: () => void;
  outcomes: ReadonlyArray<CallOutcomeOption>;
  onToggleMute: () => void;
  onHangup: () => void;
  /** Send a DTMF digit on the live call. When absent the keypad is hidden entirely. */
  onSendDigits?: (digits: string) => void;
  /**
   * "Leave voicemail" on the live call. Present only when the server allowed a drop on this call
   * (team flag, Twilio, a recorded message, the landline-only rule); absent = no button.
   */
  voicemailDrop?: VoicemailDropControl;
  onSelectOutcome: (value: string, extra?: CallOutcomeExtra) => void;
  onClose: () => void;
  className?: string;
}

export function Softphone({
  contactName,
  phoneNumber,
  status,
  durationSec,
  isMuted,
  error,
  carrierFailure,
  autoOutcome,
  isRecording,
  embeddedCarrierDialer,
  notice,
  imageSrc,
  isFirmNumber,
  callerNumber,
  callerNumberStale,
  onChangeNumber,
  outcomes,
  onToggleMute,
  onHangup,
  onSendDigits,
  voicemailDrop,
  onSelectOutcome,
  onClose,
  className,
}: SoftphoneProps) {
  const inCall = status === 'connecting' || status === 'ringing' || status === 'active';
  const finished = status === 'ended' || status === 'error';
  const view = resolvePostCallView(status, carrierFailure);

  return (
    <div className={cn('flex w-80 flex-col gap-4 rounded-xl bg-white p-5 dark:bg-gray-900', className)}>
      <ContactAvatar
        shape="circle"
        className="mx-auto size-16"
        // Avatar derives initials by splitting its alt on spaces, so the full display name
        // yields correct initials ("Ada Lovelace" → "AL"). A bare phone number would make a
        // meaningless "+" initial, so the nameless case gets a person glyph instead.
        firstName={contactName ?? null}
        lastName={null}
        imageSrc={imageSrc}
        placeholder={contactName ? undefined : <Users.User01 />}
        data-test="softphone-avatar"
      />

      <div className="flex flex-col items-center gap-1 text-center">
        <span className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
          {contactName ?? phoneNumber}
        </span>
        {contactName && <span className="truncate text-sm text-gray-500 dark:text-gray-400">{phoneNumber}</span>}
        {isFirmNumber && (
          <span
            className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-400"
            data-test="firm-number-badge">
            <General.Building02 className="size-3.5" />
            Firm number
          </span>
        )}
      </div>

      <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <span className={cn('h-2 w-2 rounded-full transition-colors', view.dotClass)} />
        <span data-test="softphone-status-label">{view.label}</span>
        {status === 'active' && <span className="tabular-nums text-gray-400">{formatDuration(durationSec)}</span>}
      </div>

      {inCall && isRecording && (
        <div className="flex items-center justify-center gap-1.5 text-xs text-red-500" data-test="recording-indicator">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          <span>Recording</span>
        </div>
      )}

      {callerNumber && (
        <CallerNumberLine
          callerNumber={callerNumber}
          stale={callerNumberStale}
          onChangeNumber={onChangeNumber}
          // Re-pinning mid-call can't affect the leg already in flight, so the pick waits for the
          // call to end (where it pins the number for every later call to this contact).
          changeDisabledReason={inCall ? 'Finish the call to change the number' : undefined}
        />
      )}

      {error && <p className="text-center text-sm text-red-500">{error}</p>}

      {notice && (
        <p className="text-center text-sm text-gray-600 dark:text-gray-300" data-test="softphone-notice">
          {notice}
        </p>
      )}

      {inCall && embeddedCarrierDialer && (
        <div className="flex flex-col items-center gap-2">
          <p
            className="text-center text-xs text-gray-500 dark:text-gray-400"
            data-test="softphone-carrier-controls-hint">
            Mute, keypad and hang up are in the JustCall dialer. When the call is over, log how it went.
          </p>
          {/* JustCall's "hang up" lives in its dialer; ours moves the rep on to the outcome step. */}
          <Button color="blue" onClick={onHangup} data-test="carrier-call-done">
            Log outcome
          </Button>
        </div>
      )}

      {inCall && !embeddedCarrierDialer && onSendDigits && (
        // Keys stay disabled until the call is answered: DTMF has no media path to ride before
        // then, and Twilio's sendDigits throws pre-media (the hook's try/catch is the backstop).
        <SoftphoneKeypad onPressDigit={onSendDigits} disabled={status !== 'active'} />
      )}

      {status === 'active' && !embeddedCarrierDialer && voicemailDrop && (
        <VoicemailDropButton control={voicemailDrop} />
      )}

      {inCall && !embeddedCarrierDialer && (
        <div className="flex items-center justify-center gap-3">
          <Button
            shape="pill"
            size="xl"
            tone={isMuted ? 'filled' : 'outline'}
            color={isMuted ? 'yellow' : 'gray'}
            disabled={status !== 'active'}
            icon={isMuted ? <Media.MicrophoneOff01 /> : <Media.Microphone01 />}
            tooltip={isMuted ? 'Unmute' : 'Mute'}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            onClick={onToggleMute}
            data-test="mute-button"
          />
          <Button
            shape="pill"
            size="xl"
            color="red"
            icon={<Communication.PhoneHangUp02 />}
            tooltip="Hang up"
            aria-label="Hang up"
            onClick={onHangup}
            data-test="hangup-button"
          />
        </div>
      )}

      {view.carrierRefused && carrierFailure && <CallFailedNotice code={carrierFailure.code} />}

      {finished && !view.blocksOutcomes && (
        <CallOutcomePicker outcomes={outcomes} autoOutcome={autoOutcome} onSelect={onSelectOutcome} onSkip={onClose} />
      )}
      {view.blocksOutcomes && (
        <Button onClick={onClose} data-test="call-failed-close">
          Close
        </Button>
      )}
    </div>
  );
}
